import { makeAutoObservable, runInAction } from 'mobx'
import type { ReactNode } from 'react'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { showToast } from 'shared/ui'
import { logger } from 'shared/lib'
import { ZIndex } from 'shared/constants/zIndex'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { uiStore } from 'shared/store/uiStore'
import { TagsApi } from 'entities/Tags'
import { ContactsApi } from 'entities/Contacts'
import { TagsModalContent } from 'pages/settings/pages/tags/ui/TagsModalContent/TagsModalContent'
import { SettingsRoutes } from 'pages/settings/route/type'

type IModalMode = 'create' | 'update' | 'delete' | 'delete_from_contacts'

export class TagsModalStore {
  private _loading = false
  private _name = ''
  private _errorsMap = new Map<string, ReactNode>()
  private _isUserCanManageTags = false
  private _idModal = ''
  private _mode: IModalMode = 'create'
  private _id: number | null = null
  private _contactIds: number[] = []
  private _bulkIds: number[] = []
  private _disposed = false
  private _isLoadingDeleteBulkModal = false

  constructor(
    isUserCanManageTags: boolean,
    private _onSuccess: () => Promise<void>,
    private _onReset: () => void
  ) {
    makeAutoObservable(this)
    this._isUserCanManageTags = isUserCanManageTags
  }

  handleCreate = () => {
    if (!this._isUserCanManageTags) return
    this._mode = 'create'
    this._id = null
    this._errorsMap.clear()
    this.openModal()
  }

  openModal = () => {
    this._idModal = nanoid()

    modalStore.addModal({
      id: this._idModal,
      title: this.isCreateMode ? 'New tag' : 'Rename',
      width: 480,
      ModalContent: () => <TagsModalContent tagsModalStore={this} />,
      onClose: this.closeModal,
      primaryAction: {
        text: this.isCreateMode ? 'Create' : 'Update',
        onAction: this.handleSave,
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => {
          this.closeModal()
        },
      },
    })
  }

  openDeleteModal = () => {
    this._idModal = nanoid()

    modalStore.addModal({
      id: this._idModal,
      showHeader: true,
      showCloseButton: false,
      showCloseIcon: false,
      width: 280,
      zIndex: ZIndex.OVERLAY_MIDDLE,
      onClose: this.closeModal,
      title: `Delete “${this._name}”?`,
      desc: 'This will remove the tag from all contacts currently using it. If this tag is part of any automations, those workflows may no longer run for these contacts. This action can’t be undone.',
      type: ModalTypeList.ALERT,
      secondaryAction: {
        text: 'Cancel',
        onAction: this.closeModal,
      },
      primaryAction: {
        text: 'Delete',
        onAction: this.handleDelete,
      },
    })
  }

  openDeleteBulkModal = async () => {
    const isSingle = this._bulkIds.length === 1
    const bulkTitle = 'Delete tags?'
    const bulkDesc =
      'These tags will be removed from all contacts currently using them. If these tags are part of any automations, those workflows may no longer run for these contacts. This action can’t be undone.'
    let singleTitle = ''
    const singleDesc =
      'This will remove the tag from all contacts currently using it. If this tag is part of any automations, those workflows may no longer run for these contacts. This action can’t be undone.'

    try {
      runInAction(() => {
        this._isLoadingDeleteBulkModal = true
      })

      if (isSingle) {
        const {
          data: { label },
        } = await TagsApi.getTagById(this._bulkIds[0])

        runInAction(() => {
          singleTitle = `Delete “${label}”?`
        })
      }
    } catch (e) {
      logger.error(e)
    } finally {
      if (this._disposed) return

      runInAction(() => {
        this._idModal = nanoid()
      })

      modalStore.addModal({
        id: this._idModal,
        showHeader: true,
        showCloseButton: false,
        showCloseIcon: false,
        width: 280,
        zIndex: ZIndex.OVERLAY_MIDDLE,
        onClose: this.closeModal,
        title: isSingle ? singleTitle || 'Delete tag?' : bulkTitle,
        desc: isSingle ? singleDesc : bulkDesc,
        type: ModalTypeList.ALERT,
        secondaryAction: {
          text: 'Cancel',
          onAction: this.closeModal,
        },
        primaryAction: {
          text: 'Delete',
          onAction: this.handleBulkDelete,
        },
      })
    }
  }

  openDeleteTagFromContactModal = () => {
    const singleDesc =
      'You’re about to remove this tag from the contact. If this tag is part of any automations, those workflows may no longer run for this contact.'
    const multiDesc =
      'You’re about to remove this tag from all selected contacts. If this tag is part of any automations, those workflows may no longer run for these contacts.'
    this._idModal = nanoid()

    modalStore.addModal({
      id: this._idModal,
      showHeader: true,
      showCloseButton: false,
      showCloseIcon: false,
      width: 280,
      zIndex: ZIndex.OVERLAY_MIDDLE,
      onClose: this.closeModal,
      title: 'Remove tag?',
      desc: this._contactIds.length === 1 ? singleDesc : multiDesc,
      type: ModalTypeList.ALERT,
      secondaryAction: {
        text: 'Cancel',
        onAction: this.closeModal,
      },
      primaryAction: {
        text: 'Remove',
        onAction: this.handleDeleteTagFromContact,
      },
    })
  }

  dispose = () => {
    this._disposed = true
    this.closeModal()
  }

  closeModal = () => {
    this._name = ''
    this._id = null
    this._contactIds = []
    this._bulkIds = []
    this._isLoadingDeleteBulkModal = false
    this.removeError('name')
    modalStore.removeModal(this._idModal)
  }

  setName = (name: string) => {
    this._name = name
  }

  setError = (key: string, error: ReactNode) => {
    this._errorsMap.set(key, error)
  }

  removeError = (key: string) => {
    this._errorsMap.delete(key)
  }

  onRename = (id: number, name: string) => {
    if (!this._isUserCanManageTags) return

    this._id = id
    this._mode = 'update'
    this._name = name
    this._errorsMap.clear()
    this.openModal()
  }

  onDelete = (id: number, name: string, isContactsPage = false) => {
    if (!this._isUserCanManageTags) return

    this._bulkIds = []
    this._id = id
    this._mode = isContactsPage ? 'delete_from_contacts' : 'delete'
    this._name = name
    this._errorsMap.clear()
    this.openDeleteModal()
  }

  onDeleteTagFromContact = (id: number, contactIds: number[]) => {
    if (!this._isUserCanManageTags) return

    this._bulkIds = []
    this._id = id
    this._contactIds = contactIds
    this._mode = 'delete'
    this._errorsMap.clear()
    this.openDeleteTagFromContactModal()
  }

  onDeleteBulk = (ids: number[]) => {
    if (!this._isUserCanManageTags) return

    this._id = null
    this._bulkIds = ids
    this._mode = 'delete'
    this._errorsMap.clear()
    this.openDeleteBulkModal()
  }

  handleSave = async () => {
    try {
      if (!this._name.trim().length) {
        this.setError('name', 'Please enter a tag name')
        return
      }

      if (this._loading) return

      runInAction(() => {
        this._loading = true
      })

      this.removeError('name')

      if (this.isCreateMode) {
        await TagsApi.createTag({ label: this.name.trim() })
      } else if (this._id !== null) {
        await TagsApi.updateTagById(this._id, { label: this.name.trim() })
      }

      await this._onSuccess()
      this.closeModal()

      showToast({
        type: 'success',
        title: this.isCreateMode ? 'Tag created' : 'Tag name updated',
      })
    } catch (e) {
      logger.error(e)

      if (e instanceof AxiosError) {
        if (e.errors.getError('label')) {
          this.setError('name', e.errors.getError('label'))
        } else {
          showToast({
            type: 'error',
            title: 'Something went wrong. Please try again',
          })
        }
      }
    } finally {
      this._loading = false
    }
  }

  handleDelete = async () => {
    try {
      if (this._loading || this._id === null) return

      runInAction(() => {
        this._loading = true
      })

      await TagsApi.deleteTagById(this._id)
      this._onReset()
      this.closeModal()

      if (this._mode === 'delete_from_contacts') {
        uiStore.changeRouteViaVue({
          path: `/${SettingsRoutes.root}/${SettingsRoutes.tags}`,
        })
      } else {
        await this._onSuccess()
      }

      showToast({
        type: 'success',
        title: 'Tag deleted',
      })
    } catch (error) {
      logger.error(error)

      if (error instanceof AxiosError) {
        showToast({
          type: 'error',
          title: 'Something went wrong. Please try again',
        })
      }
    } finally {
      this._loading = false
    }
  }

  handleBulkDelete = async () => {
    try {
      if (this._loading || !this._bulkIds.length) return

      runInAction(() => {
        this._loading = true
      })

      const isSingle = this._bulkIds.length === 1

      await TagsApi.deleteBulkTags(this._bulkIds)
      this._onReset()
      this.closeModal()
      await this._onSuccess()

      showToast({
        type: 'success',
        title: `Tag${isSingle ? '' : 's'} deleted`,
      })
    } catch (error) {
      logger.error(error)

      if (error instanceof AxiosError) {
        showToast({
          type: 'error',
          title: 'Something went wrong. Please try again',
        })
      }
    } finally {
      this._loading = false
    }
  }

  handleDeleteTagFromContact = async () => {
    try {
      if (this._loading || this._id === null || !this._contactIds.length) return

      runInAction(() => {
        this._loading = true
      })

      await ContactsApi.deleteContactsTags({
        tags: [this._id],
        contact_ids: this._contactIds,
      })

      const contactsQty = this._contactIds.length

      this._onReset()
      this.closeModal()
      await this._onSuccess()

      showToast({
        type: 'success',
        title:
          contactsQty === 1
            ? 'Tag removed from contact'
            : `Tag removed from ${contactsQty} contacts`,
      })
    } catch (error) {
      logger.error(error)

      if (error instanceof AxiosError) {
        showToast({
          type: 'error',
          title: 'Something went wrong. Please try again',
        })
      }
    } finally {
      this._loading = false
    }
  }

  get errorsMap() {
    return this._errorsMap
  }

  get name() {
    return this._name
  }

  get isCreateMode() {
    return this._mode === 'create'
  }

  get isLoadingDeleteBulkModal() {
    return this._isLoadingDeleteBulkModal
  }
}
